import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextV2,
  SvgLoginKeyBold,
} from '@insights-ltd/design-library/components';
import { Box, styled } from '@mui/material';
import { getTheme } from '@insights-ltd/design-library/themes';
import { AiFeatureSection } from './AiFeatureSection';
import { SaveState } from './types';

const StyledIconWrapper = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: theme.palette.pink.light,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}));

const localTheme = getTheme();

type FeatureAccessSectionProps = {
  aiEnabled: boolean;
  onAiToggle: (enabled: boolean) => Promise<void>;
};

export function FeatureAccessSection({
  aiEnabled,
  onAiToggle,
}: FeatureAccessSectionProps) {
  const { t } = useTranslation();
  const [isAiEnabled, setIsAiEnabled] = useState(aiEnabled);
  const [aiSaveState, setAiSaveState] = useState<SaveState>('idle');

  useEffect(() => {
    setIsAiEnabled(aiEnabled);
  }, [aiEnabled]);

  const handleAiToggle = async (value: boolean) => {
    setIsAiEnabled(value);
    setAiSaveState('idle');

    try {
      await onAiToggle(value);
      setAiSaveState('saved');
    } catch {
      setAiSaveState('error');
      setIsAiEnabled(!value);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        <StyledIconWrapper>
          <SvgLoginKeyBold
            width={20}
            height={20}
            fill={localTheme.brandColours.darkPink}
          />
        </StyledIconWrapper>
        <TextV2 variant="h4">
          {t('ui.event-management.organisations.feature-access.title')}
        </TextV2>
      </Box>
      <TextV2 color="textSecondary" sx={{ mb: 2 }}>
        {t('ui.event-management.organisations.feature-access.description')}
      </TextV2>

      <AiFeatureSection
        isEnabled={isAiEnabled}
        saveState={aiSaveState}
        onToggle={handleAiToggle}
      />
    </Box>
  );
}
